import { useMemo } from "react";
import {
  createDaily,
  createIndexedWeekDay,
  createMonthly,
  createWeekDays,
  createWeekly,
  formatRecurrence,
  isChangeableToEmptyRecurrence,
  Recurrence,
  RecurringOptions,
} from "../../../../domains/Recurrence";
import SelectField from "../../../atoms/DeprecatedSelectField";

type RecurrenceOption = {
  label: string;
  value: RecurringOptions | null;
};

type Props = {
  value: RecurringOptions | null;
  date: Date;
  onChange: (option: RecurrenceOption | null) => void;
  currentRecurrence: Recurrence | null;
  disabled?: boolean;
};

export const RecurrenceSelect = ({
  value,
  date,
  onChange,
  currentRecurrence,
  disabled = false,
}: Props) => {
  const optionsTable = useMemo(() => {
    return createRecurrencesTable(
      date,
      currentRecurrence ? currentRecurrence.until : null,
    );
  }, [date.toUTCString()]);

  const options = useMemo(
    () => createOptions(optionsTable, currentRecurrence),
    [optionsTable],
  );

  return (
    <SelectField
      value={value}
      options={options}
      onChange={onChange}
      defaultValue={options[0]}
      isSearchable={false}
      placeholder=""
      disabled={disabled}
      hideIndicator
      className="w-[16.4rem]"
    />
  );
};

const createOptions = (
  recurrencesTable: Record<RecurringOptions, Recurrence>,
  currentRecurrence: Props["currentRecurrence"],
) => {
  const allowIncludeEmptyOption =
    isChangeableToEmptyRecurrence(currentRecurrence);
  const recurrences = [
    ...(allowIncludeEmptyOption ? [null] : []),
    ...RecurringOptions,
  ];

  return recurrences.map((r) => {
    return {
      label: formatRecurrence(r ? recurrencesTable[r] : null, true),
      value: r,
    };
  });
};

const createRecurrencesTable = (
  date: Date,
  until: Date | null,
): Record<RecurringOptions, Recurrence> => {
  return {
    daily: createDaily(until),
    weekly: createWeekly(date, until),
    weekdays: createWeekDays(until),
    indexedWeekDay: createIndexedWeekDay(date, until),
    monthly: createMonthly(date, until),
  };
};
